import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "sparkloop-merchant-kiosk-lock";

/**
 * Kiosk Lock Mode for the merchant dashboard.
 *
 * When ENABLED, the device acts as a one-app kiosk:
 *  - Sign-out button is hidden
 *  - If a clerk pulls-to-refresh, they're auto-logged-in again
 *    (the JWT is persisted across reloads — same as today — so we just hide
 *    the unauth fallback by rerouting to /merchant)
 *  - Settings page is hidden (so they can't toggle it off there)
 *  - To unlock: long-press the SparkLoop sidebar logo for 5 seconds, then
 *    re-enter the merchant password
 *
 * Stored in localStorage so it survives PWA relaunches and tablet reboots.
 */
export function useKioskLock() {
  const [locked, setLocked] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  // React if another tab toggles it.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setLocked(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const lock = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setLocked(true);
  }, []);

  const unlock = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setLocked(false);
  }, []);

  return { locked, lock, unlock };
}
